/**
 * AuthView - Login UI Component
 * 
 * Clean, white-themed authentication interface for Maestro.
 * Features:
 * - Email/password sign in
 * - Password reset flow
 * - "Start Free Trial" link opens Stripe checkout slideout
 * - Form validation, loading states, error handling
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isValidRefreshToken } from '../lib/authTokens';
import { ValetIcon } from './icons/ValetIcon';
import StripeCheckoutSlideout from './StripeCheckoutSlideout';
import { resetRefreshState } from '../services/api';

let authListeners = [];
let authState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  adminRole: null,
  adminPermissions: null,
  isLoading: true,
  error: null,
};

let _refreshAlreadyFailed = false;

function markRefreshFailed() {
  _refreshAlreadyFailed = true;
  try { sessionStorage.setItem('maestro_refresh_failed', '1'); } catch {}
}

function isRefreshBlocked() {
  if (_refreshAlreadyFailed) return true;
  try { return sessionStorage.getItem('maestro_refresh_failed') === '1'; } catch { return false; }
}

function clearRefreshBlock() {
  _refreshAlreadyFailed = false;
  try { sessionStorage.removeItem('maestro_refresh_failed'); } catch {}
}

export function getAuthState() {
  return authState;
}

/** Read maestro_auth from localStorage (may be fresher than in-memory authState). */
export function getStoredAuthRecord() {
  try {
    const raw = localStorage.getItem('maestro_auth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Sync in-memory auth from localStorage after api.ts token refresh. */
function syncAuthFromLocalStorage() {
  const stored = getStoredAuthRecord();
  if (!stored?.accessToken) return;
  const updates = {};
  if (stored.accessToken && stored.accessToken !== authState.accessToken) {
    updates.accessToken = stored.accessToken;
  }
  if (stored.refreshToken && stored.refreshToken !== authState.refreshToken) {
    updates.refreshToken = stored.refreshToken;
  }
  if (Object.keys(updates).length > 0) {
    setAuthState(updates);
  }
}

function decodeJwtExpMs(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expSec = Number(payload?.exp || 0);
    if (!Number.isFinite(expSec) || expSec <= 0) return null;
    return expSec * 1000;
  } catch {
    return null;
  }
}

export function subscribeToAuth(listener) {
  authListeners.push(listener);
  return () => {
    authListeners = authListeners.filter(l => l !== listener);
  };
}

function notifyAuthListeners() {
  authListeners.forEach(l => l(authState));
}

let _restoreFromAuthJsonAbort = false;

/** Re-export for callers that import from AuthView. */
export { isValidRefreshToken } from '../lib/authTokens';

function pickValidRefreshToken(...candidates) {
  for (const t of candidates) {
    if (isValidRefreshToken(t)) return t;
  }
  return null;
}

function extractSessionTokens(data) {
  const session = data?.session || null;
  const accessToken =
    session?.access_token || session?.accessToken ||
    data?.access_token || data?.accessToken || null;
  const refreshToken = pickValidRefreshToken(
    session?.refresh_token,
    data?.refresh_token,
    session?.refreshToken,
    data?.refreshToken,
  );
  if (!refreshToken) {
    const rejected = session?.refresh_token || data?.refresh_token;
    if (rejected && !isValidRefreshToken(rejected)) {
      console.warn(`[Auth] Ignoring invalid refresh_token (len=${rejected.length})`);
    }
  }
  return { accessToken, refreshToken };
}

function persistAuthToLocalStorage() {
  if (!authState.accessToken) {
    localStorage.removeItem('maestro_auth');
    return;
  }
  localStorage.setItem('maestro_auth', JSON.stringify({
    accessToken: authState.accessToken,
    refreshToken: authState.refreshToken || null,
    user: authState.user,
    adminRole: authState.adminRole || null,
    adminPermissions: authState.adminPermissions || null,
  }));
}

function syncDaemonCredentials(refreshToken, userId, email) {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  const len = (refreshToken || '').length;
  console.log(`[Auth] syncDaemonCredentials refresh_token length=${len}`);
  if (!isValidRefreshToken(refreshToken)) {
    console.error(`[Auth] refresh_token too short (${len}), skipping writeAuthJson`);
    return;
  }
  import('../lib/connector')
    .then(c => c.writeAuthJson(refreshToken, userId || '', email || ''))
    .catch(e => console.error('[Auth] writeAuthJson failed:', e));
}

function setAuthState(updates) {
  authState = { ...authState, ...updates };
  notifyAuthListeners();
  
  const persistKeys = ['accessToken', 'refreshToken', 'user', 'adminRole', 'adminPermissions'];
  if (persistKeys.some(k => updates[k] !== undefined)) {
    persistAuthToLocalStorage();
  }
}

export async function refreshAuthSession() {
  if (isRefreshBlocked()) {
    throw new Error('Refresh already failed this session');
  }

  const refreshToken = authState.refreshToken || getStoredAuthRecord()?.refreshToken;
  if (!isValidRefreshToken(refreshToken)) {
    throw new Error('No refresh token');
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.session?.access_token) {
      markRefreshFailed();
      throw new Error(data?.error || 'Token refresh failed');
    }

    const newToken = data.session.access_token;
    const newRefresh = data.session.refresh_token || refreshToken;
    if (!isValidRefreshToken(newRefresh)) {
      markRefreshFailed();
      throw new Error('Token refresh returned invalid refresh token');
    }
    setAuthState({
      isAuthenticated: true,
      accessToken: newToken,
      refreshToken: newRefresh,
      isLoading: false,
      error: null,
    });

    syncDaemonCredentials(newRefresh, authState.user?.id, authState.user?.email);

    try {
      const userRes = await fetch(`${API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.adminRole) {
          setAuthState({ adminRole: userData.adminRole, adminPermissions: userData.adminPermissions || null });
        }
      }
    } catch {}

    return data.session;
  } catch (e) {
    markRefreshFailed();
    await signOut();
    throw e;
  }
}

const API_URL = 'https://api.maestroaudio.net';

function initializeAuth() {
  _restoreFromAuthJsonAbort = false;
  if (isRefreshBlocked()) {
    console.log('[Auth] refresh previously failed this session — clearing stale auth, showing login');
    localStorage.removeItem('maestro_auth');
    setAuthState({ isAuthenticated: false, accessToken: null, refreshToken: null, user: null, isLoading: false, error: null });
    return false;
  }

  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      let { accessToken, refreshToken, user, adminRole, adminPermissions } = parsed;
      // Guard against corrupt storage (e.g. literal "refreshToken" string — 12 chars).
      if (!isValidRefreshToken(refreshToken)) {
        console.warn(`[Auth] Stored refresh_token invalid (len=${(refreshToken || '').length}) — will try auth.json or re-login`);
        refreshToken = null;
      }
      setAuthState({ 
        isAuthenticated: true, 
        accessToken, 
        refreshToken, 
        user,
        adminRole: adminRole || null,
        adminPermissions: adminPermissions || null,
        isLoading: false 
      });

      const expMs = decodeJwtExpMs(accessToken);
      const now = Date.now();
      if (expMs && expMs - now < 2 * 60 * 1000 && refreshToken) {
        refreshAuthSession().catch(() => {});
      } else if (accessToken) {
        fetch(`${API_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then(r => r.ok ? r.json() : null).then(d => {
          if (d?.adminRole) {
            setAuthState({ adminRole: d.adminRole, adminPermissions: d.adminPermissions || null });
          }
        }).catch(() => {});
      }
      // Invalid refresh token in localStorage — try auth.json on disk (Tauri).
      if (!isValidRefreshToken(refreshToken) && typeof window !== 'undefined' && '__TAURI__' in window && accessToken) {
        restoreFromAuthJson();
        return false;
      }
      return true;
    }
  } catch (e) {
    console.error('Failed to restore auth state:', e);
  }

  // Tauri fallback: WKWebView may clear localStorage between launches.
  // If auth.json exists on disk (written by the Tauri app or daemon), use
  // its refresh token to restore the session without requiring re-login.
  // Skip if a refresh already failed this session — go straight to login.
  if (typeof window !== 'undefined' && '__TAURI__' in window) {
    if (isRefreshBlocked()) {
      console.log('[Auth] refresh previously failed this session — skipping auth.json, showing login');
      setAuthState({ isAuthenticated: false, accessToken: null, refreshToken: null, user: null, isLoading: false, error: null });
      return false;
    }
    restoreFromAuthJson();
    return false;
  }

  setAuthState({ isLoading: false });
  return false;
}

async function restoreFromAuthJson() {
  if (_restoreFromAuthJsonAbort) {
    setAuthState({ isLoading: false });
    return;
  }
  try {
    const { readAuthJson } = await import('../lib/connector');
    if (_restoreFromAuthJsonAbort) {
      setAuthState({ isLoading: false });
      return;
    }
    const creds = await readAuthJson();
    if (_restoreFromAuthJsonAbort) {
      setAuthState({ isLoading: false });
      return;
    }
    if (!isValidRefreshToken(creds?.refresh_token)) {
      console.log('[Auth] auth.json missing or invalid — keeping current session if any');
      return;
    }
    console.log('[Auth] restoring session from auth.json for', creds.email);

    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: creds.refresh_token }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.session?.access_token) {
      console.warn('[Auth] auth.json refresh failed — keeping current session');
      return;
    }

    const newAccess = data.session.access_token;
    const newRefresh = data.session.refresh_token || creds.refresh_token;
    setAuthState({
      isAuthenticated: true,
      accessToken: newAccess,
      refreshToken: newRefresh,
      user: data.user || { id: creds.user_id, email: creds.email },
      isLoading: false,
      error: null,
    });

    syncDaemonCredentials(newRefresh, creds.user_id, creds.email);
    console.log('[Auth] session restored from auth.json');
  } catch (e) {
    console.error('[Auth] restoreFromAuthJson failed:', e);
    markRefreshFailed();
    localStorage.removeItem('maestro_auth');
    setAuthState({ isAuthenticated: false, accessToken: null, refreshToken: null, user: null, isLoading: false, error: null });
  } finally {
    if (!authState.isAuthenticated) {
      setAuthState({ isLoading: false });
    }
  }
}

initializeAuth();

// Keep authState in sync when api.ts refreshes tokens in localStorage only.
if (typeof window !== 'undefined') {
  window.addEventListener('maestro:token-refreshed', syncAuthFromLocalStorage);
}

/** Obtain a valid refresh token for the background daemon (localStorage → auth.json → API refresh). */
export async function resolveRefreshTokenForDaemon() {
  syncAuthFromLocalStorage();
  const stored = getStoredAuthRecord();
  let refreshToken = authState.refreshToken || stored?.refreshToken || null;
  const accessToken = authState.accessToken || stored?.accessToken || null;
  const userId = authState.user?.id || stored?.user?.id || '';
  const email = authState.user?.email || stored?.user?.email || '';

  if (isValidRefreshToken(refreshToken)) {
    return { refreshToken, accessToken, userId, email };
  }

  if (typeof window !== 'undefined' && '__TAURI__' in window) {
    try {
      const { readAuthJson } = await import('../lib/connector');
      const creds = await readAuthJson();
      if (isValidRefreshToken(creds?.refresh_token)) {
        setAuthState({
          refreshToken: creds.refresh_token,
          user: authState.user || { id: creds.user_id, email: creds.email },
        });
        return {
          refreshToken: creds.refresh_token,
          accessToken,
          userId: creds.user_id || userId,
          email: creds.email || email,
        };
      }
    } catch {}
  }

  const attempt = refreshToken || stored?.refreshToken;
  if (attempt && attempt.length >= 4 && !isRefreshBlocked()) {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: attempt }),
      });
      const data = await response.json().catch(() => null);
      if (response.ok && isValidRefreshToken(data?.session?.refresh_token)) {
        setAuthState({
          isAuthenticated: true,
          accessToken: data.session.access_token,
          refreshToken: data.session.refresh_token,
          user: authState.user || data.user,
          isLoading: false,
        });
        return {
          refreshToken: data.session.refresh_token,
          accessToken: data.session.access_token,
          userId,
          email,
        };
      }
    } catch {}
  }

  return { refreshToken: null, accessToken, userId, email };
}

export async function signUp(email, password, metadata) {
  setAuthState({ isLoading: true, error: null });
  
  try {
    const response = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, metadata }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      const err = new Error(data.error || 'Sign up failed');
      err.code = data.code || null;
      throw err;
    }
    
    if (data.session) {
      clearRefreshBlock();
      resetRefreshState();
      const { accessToken, refreshToken } = extractSessionTokens(data);
      setAuthState({
        isAuthenticated: true,
        user: data.user,
        accessToken,
        refreshToken,
        isLoading: false,
      });
      if (refreshToken) {
        syncDaemonCredentials(refreshToken, data.user?.id, email);
      } else {
        console.error('[Auth] signup returned no valid refresh_token — remote library service may not persist');
      }
    } else {
      setAuthState({ isLoading: false });
      return { needsConfirmation: true, message: data.message };
    }
    
    return { success: true, user: data.user };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

async function signIn(email, password) {
  _restoreFromAuthJsonAbort = true;
  setAuthState({ isLoading: true, error: null });
  
  try {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      throw new Error('Unable to reach the server. Please check your internet connection and try again.');
    }

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('Server returned an unexpected response. Please try again.');
    }
    
    if (!response.ok) {
      const errMsg = data?.error || 'Sign in failed';
      if (errMsg.toLowerCase().includes('invalid login')) {
        throw new Error('Incorrect email or password. Please try again, or use "Forgot password?" to reset.');
      }
      throw new Error(errMsg);
    }

    if (!data?.session?.access_token && !data?.access_token) {
      throw new Error('Sign in failed — no session returned. Please try again.');
    }

    clearRefreshBlock();
    resetRefreshState();

    const { accessToken, refreshToken } = extractSessionTokens(data);
    console.log(`[Auth] signIn tokens: access=${accessToken?.length || 0} refresh=${refreshToken?.length || 0}`);

    setAuthState({
      isAuthenticated: true,
      user: data.user,
      accessToken,
      refreshToken,
      isLoading: false,
    });

    if (refreshToken) {
      syncDaemonCredentials(refreshToken, data.user?.id, email);
    } else {
      console.error('[Auth] signIn returned no valid refresh_token — sign out and sign in again, or contact support');
      setAuthState({
        error: 'Signed in, but remote library access needs a fresh login. Please sign out and sign in once more.',
      });
    }

    // Fetch admin role in background (non-blocking)
    try {
      const userRes = await fetch(`${API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.adminRole) {
          setAuthState({ adminRole: userData.adminRole, adminPermissions: userData.adminPermissions || null });
        }
      }
    } catch {}
    
    return { success: true, user: data.user };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

export async function signOut() {
  _restoreFromAuthJsonAbort = true;
  clearRefreshBlock();
  const token = authState.accessToken;

  // Unregister from peer network before clearing auth
  try {
    const machineId = localStorage.getItem('maestro_machine_id');
    if (machineId && token) {
      const { unregisterPeer } = await import('../hooks/usePeerNetwork');
      await unregisterPeer(machineId);
    }
  } catch {}
  
  setAuthState({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
    adminRole: null,
    adminPermissions: null,
    isLoading: false,
  });
  
  if (token) {
    try {
      await fetch(`${API_URL}/api/auth/signout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // Ignore - local signout is sufficient
    }
  }
}

async function resetPassword(email) {
  setAuthState({ isLoading: true, error: null });
  
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Password reset failed');
    }
    
    setAuthState({ isLoading: false });
    return { success: true };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

export function handleEmailConfirmationRedirect() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');

  if (accessToken && (type === 'signup' || type === 'magiclink' || type === 'email')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    clearRefreshBlock();

    setAuthState({
      isAuthenticated: true,
      accessToken,
      refreshToken: refreshToken || null,
      user: null,
      isLoading: false,
    });

    return { confirmed: true, type };
  }

  return null;
}

export function useAuth() {
  const [state, setState] = useState(() => authState);

  useEffect(() => {
    setState(authState);
    return subscribeToAuth((newState) => {
      console.log('[Auth] State update:', { isAuth: newState.isAuthenticated, email: newState.user?.email });
      setState(newState);
    });
  }, []);

  return state;
}

// =============================================================================
// AUTHVIEW COMPONENT
// =============================================================================

const GOLD = '#C9A84C';

export default function AuthView({ onAuthenticated, isModal = false }) {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [stripeSlideout, setStripeSlideout] = useState({ open: false, clientSecret: null });
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const handleResendConfirmation = useCallback(async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setError(null);
        setNeedsConfirmation(false);
        setSuccess('Confirmation email sent! Check your inbox (and spam folder), then click the link to confirm your account.');
      } else {
        setError(data?.error || 'Failed to resend confirmation email.');
      }
    } catch {
      setError('Unable to resend. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  useEffect(() => {
    const result = handleEmailConfirmationRedirect();
    if (result?.confirmed) {
      setMode('confirmed');
      const timer = setTimeout(() => {
        onAuthenticated?.();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const openStripeCheckout = useCallback(async () => {
    try {
      const token = (() => {
        try {
          const stored = localStorage.getItem('maestro_auth');
          if (stored) return JSON.parse(stored).accessToken || null;
        } catch {}
        return null;
      })();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const r = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST', headers,
        body: JSON.stringify({ ui_mode: 'embedded' }),
      });
      const d = await r.json();
      if (d.clientSecret) {
        setStripeSlideout({ open: true, clientSecret: d.clientSecret });
      }
    } catch (e) { console.error('[Stripe]', e); }
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setNeedsConfirmation(false);
    setIsLoading(true);
    
    try {
      if (mode === 'signin') {
        try {
          await signIn(email, password);
          onAuthenticated?.();
        } catch (err) {
          const msg = err.message?.toLowerCase() || '';
          if (msg.includes('email not confirmed') || msg.includes('not confirmed') || msg.includes('confirm your email')) {
            setNeedsConfirmation(true);
            throw new Error('Your email address has not been confirmed yet. Please check your inbox (and spam folder) for the confirmation link, or click the button below to resend it.');
          }
          throw err;
        }
      } else if (mode === 'signup') {
        const result = await signUp(email, password);
        if (result?.needsConfirmation) {
          setSuccess(result.message || 'Check your inbox for a confirmation link.');
          setMode('signin');
        } else {
          openStripeCheckout();
        }
      } else if (mode === 'reset') {
        await resetPassword(email);
        setSuccess('Password reset email sent. Check your inbox.');
        setMode('signin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    color: '#111',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = GOLD;
    e.target.style.boxShadow = `0 0 0 3px ${GOLD}20`;
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#e0e0e0';
    e.target.style.boxShadow = 'none';
  };

  // ---- Email Confirmed Welcome Screen ----
  if (mode === 'confirmed') {
    return (
      <div style={{
        minHeight: isModal ? 'auto' : '100vh',
        background: isModal ? 'transparent' : '#f8f8f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isModal ? '0' : '20px',
        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: '#ffffff',
          borderRadius: '24px',
          padding: '48px 40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '20px',
            background: 'transparent', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 24px',
          }}>
            <ValetIcon size={72} className="rounded-[20px]" />
          </div>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(34, 197, 94, 0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{
            fontSize: '24px', fontWeight: '700', color: '#111',
            margin: '0 0 8px', letterSpacing: '-0.3px',
          }}>
            Email confirmed!
          </h2>
          <p style={{ color: '#888', fontSize: '15px', margin: 0 }}>
            Welcome to Maestro.
          </p>
          <div style={{ marginTop: '24px' }}>
            <div style={{
              width: '32px', height: '32px',
              border: `3px solid ${GOLD}30`,
              borderTopColor: GOLD, borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto',
            }} />
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ---- Standard Sign In / Reset Form ----
  return (
    <div style={{
      minHeight: isModal ? 'auto' : '100vh',
      background: isModal ? 'transparent' : '#f8f8f8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isModal ? '0' : '20px',
      fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Auth card */}
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: '#ffffff',
        borderRadius: '24px',
        padding: isModal ? '32px' : '40px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo/branding */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '20px',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <ValetIcon size={72} className="rounded-[20px]" />
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#111',
            margin: 0,
            letterSpacing: '-0.5px',
          }}>
            Maestro
          </h1>
          <p style={{
            color: '#999',
            fontSize: '14px',
            margin: '8px 0 0',
          }}>
            {mode === 'signin' && 'Welcome back'}
            {mode === 'signup' && 'Start your free 30-day trial'}
            {mode === 'reset' && 'Reset your password'}
          </p>
        </div>
        
        {/* Error/Success messages */}
        {error && (
          <div ref={errorRef} style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0, lineHeight: 1.3 }}>⚠️</span>
              <span style={{ color: '#dc2626', fontSize: '14px', lineHeight: 1.4 }}>{error}</span>
            </div>
            {needsConfirmation && (
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={isLoading}
                style={{
                  marginTop: '12px',
                  width: '100%',
                  padding: '10px',
                  background: GOLD,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#1a1a2e',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                Resend Confirmation Email
              </button>
            )}
          </div>
        )}
        
        {success && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            <span style={{ color: '#16a34a', fontSize: '14px' }}>{success}</span>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: '#555',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '8px',
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={inputStyle}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </div>
          
          {(mode === 'signin' || mode === 'signup') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#555',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Choose a password' : '••••••••'}
                required
                minLength={mode === 'signup' ? 6 : undefined}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          )}
          
          {mode === 'signin' && (
            <div style={{ marginBottom: '20px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => { setMode('reset'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '14px',
              background: isLoading
                ? `${GOLD}80`
                : `linear-gradient(135deg, ${GOLD}, #E8C94A, ${GOLD})`,
              border: 'none',
              borderRadius: '12px',
              color: '#1a1a2e',
              fontSize: '15px',
              fontWeight: '700',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: isLoading ? 'none' : `0 4px 16px ${GOLD}40`,
            }}
          >
            {isLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(26, 26, 46, 0.3)',
                  borderTopColor: '#1a1a2e',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Loading...
              </span>
            ) : (
              <>
                {mode === 'signin' && 'Sign In'}
                {mode === 'signup' && 'Create Account & Start Trial'}
                {mode === 'reset' && 'Send Reset Link'}
              </>
            )}
          </button>
        </form>
        
        {/* Mode toggle */}
        <div style={{
          marginTop: '24px',
          textAlign: 'center',
          color: '#999',
          fontSize: '14px',
        }}>
          {mode === 'signin' && (
            <>
              Don&rsquo;t have an account?{' '}
              <button
                onClick={() => { setMode('signup'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Start Your Free 30-Day Trial &rarr;
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('signin'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
          {mode === 'reset' && (
            <>
              Remember your password?{' '}
              <button
                onClick={() => { setMode('signin'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>

      <StripeCheckoutSlideout
        open={stripeSlideout.open}
        onClose={() => setStripeSlideout({ open: false, clientSecret: null })}
        clientSecret={stripeSlideout.clientSecret}
        showOnboarding
      />
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
